import { getSWRTaxLedgerValue, sellSWRToRebalance } from './swrTaxModel';

export const SWR_DEBT_ACCOUNTING_VERSION = 'italian-per-isin-proportional-debt-v1';
export const SWR_DEBT_POLICY = 'proportional-sales-contributions-debt-first-v1';
export const createSWRDebtConfig = () => ({ policy: SWR_DEBT_POLICY });
export function normalizeSWRDebtConfig(config) {
  if (!config || config.policy !== SWR_DEBT_POLICY || Object.keys(config).some(k => k !== 'policy')) {
    throw new Error('Regole del debito non disponibili: nessuna sostituzione automatica.');
  }
  return { policy: config.policy };
}

export function createSWRDebtState(principal) {
  if (!Number.isFinite(principal) || principal < 0) throw new Error('Debito iniziale non valido.');
  return { principal, unpaidInterest: 0, interestAccrued: 0, interestPaid: 0, principalRepaid: 0 };
}
export const getSWRDebtLiability = state => state.principal + state.unpaidInterest;
export const getSWRDebtEquity = (ledger, state, cash = 0) => getSWRTaxLedgerValue(ledger) + cash - getSWRDebtLiability(state);

// Arrears stay payable but never become principal or generate interest on interest.
// This is a declared monthly model convention, not a lender's loan contract.
export function accrueSWRDebtInterest(state, rate) {
  if (!Number.isFinite(rate) || rate < 0) throw new Error('Manca un tasso mensile valido per il debito.');
  const amount = state.principal * rate;
  if (!Number.isFinite(amount + state.unpaidInterest)) throw new Error('Interessi del debito oltre la capacità numerica.');
  state.unpaidInterest += amount; state.interestAccrued += amount;
  return amount;
}

// External contributions and insolvency liquidation settle interest first, then
// principal. No new credit is drawn when the debt has been repaid.
export function paySWRDebtLiabilities(state, amount, principal = true) {
  if (!Number.isFinite(amount) || amount < 0) throw new Error('Flusso per il debito non valido.');
  const interestPaid = Math.min(amount, state.unpaidInterest);
  state.unpaidInterest -= interestPaid; state.interestPaid += interestPaid;
  const principalRepaid = principal ? Math.min(amount - interestPaid, state.principal) : 0;
  state.principal -= principalRepaid; state.principalRepaid += principalRepaid;
  return { remaining: amount - interestPaid - principalRepaid, interestPaid, principalRepaid };
}

// For each necessary sale, repay D*g/A. The production tax solver funds the
// remaining net without a monthly reset of leverage or a second tax deduction.
export function sellSWRWithDebt(ledger, state, targetNet, year, cashAvailable = 0) {
  if (!Number.isFinite(cashAvailable) || cashAvailable < 0) throw new Error('Cash disponibile per il debito non valido.');
  const assets = getSWRTaxLedgerValue(ledger);
  const ratio = assets > 0 ? state.principal / assets : 0;
  // Above 100% D/A no asset-only sale can release positive equity. Selling the
  // entire basket is still possible: cash must supplement the bank repayment.
  const sale = sellSWRToRebalance(ledger, targetNet, year, Math.min(ratio, 1));
  const proceeds = Math.max(0, sale.grossWithdrawal - sale.tax);
  const desiredRepayment = sale.grossWithdrawal > 0 ? Math.min(state.principal, ratio * sale.grossWithdrawal) : 0;
  const cashUsedForDebt = Math.min(cashAvailable, Math.max(0, desiredRepayment - proceeds));
  const principalRepaid = Math.min(desiredRepayment, proceeds + cashUsedForDebt);
  state.principal -= principalRepaid; state.principalRepaid += principalRepaid;
  return { ...sale, netReceived: Math.max(0, proceeds - principalRepaid), principalRepaid, cashUsedForDebt };
}

export function liquidateSWRDebtPortfolio(ledger, state, cash, year) {
  const sale = sellSWRToRebalance(ledger, Number.MAX_VALUE, year);
  const paid = paySWRDebtLiabilities(state, cash + sale.netReceived);
  return { ...sale, ...paid, cash: paid.remaining };
}

export function attachSWRDebtSnapshot(snapshot, state) {
  const liabilities = getSWRDebtLiability(state);
  return { ...snapshot, netLiquidationValue: snapshot.netLiquidationValue - liabilities,
    equity: snapshot.invested + snapshot.cash - liabilities,
    debt: { ...state, liabilities,
      leverage: snapshot.invested + snapshot.cash > liabilities
        ? (snapshot.invested + snapshot.cash) / (snapshot.invested + snapshot.cash - liabilities) : null } };
}
